import { ActionType, createReducer } from 'typesafe-actions';
import { authDashboardActions } from '../authDashboard/actions';
import { comoLinkActions } from './actions';
import { ComoLinkState, ProgramLinkState } from './types';

const initialState: ComoLinkState = {};

const emptyProgram = (entryId: string): ProgramLinkState => ({
  entryId,
  loading: true,
  merchants: [],
  stateError: false,
  navigatingMsk: null,
  handoffError: null,
  retryAt: null,
  openRequests: 0
});

const patchCurrent = (
  state: ComoLinkState,
  { programId, entryId }: { programId: string; entryId: string },
  patch: Partial<ProgramLinkState>
): ComoLinkState => {
  const current = state[programId];
  if (!current || current.entryId !== entryId) return state;
  return { ...state, [programId]: { ...current, ...patch } };
};

export const comoLinkReducer = createReducer<
  ComoLinkState,
  ActionType<typeof comoLinkActions | typeof authDashboardActions>
>(initialState)
  .handleAction(comoLinkActions.fetchState.request, (state, { payload }) =>
    state[payload.programId]?.entryId === payload.entryId
      ? state
      : { ...state, [payload.programId]: emptyProgram(payload.entryId) }
  )
  .handleAction(comoLinkActions.fetchState.success, (state, { payload }) =>
    patchCurrent(state, payload, { loading: false, stateError: false, merchants: payload.merchants })
  )
  .handleAction(comoLinkActions.fetchState.failure, (state, { payload }) =>
    patchCurrent(state, payload, { loading: false, stateError: true })
  )
  .handleAction(comoLinkActions.retryState, (state, { payload }) =>
    patchCurrent(state, payload, { loading: true, stateError: false })
  )
  .handleAction(comoLinkActions.requestMagicLink, (state, { payload }) =>
    state[payload.programId]?.navigatingMsk
      ? state
      : patchCurrent(state, payload, { navigatingMsk: payload.merchantSequenceKey, handoffError: null, retryAt: null })
  )
  .handleAction(comoLinkActions.handoffFailed, (state, { payload }) =>
    patchCurrent(state, payload, { navigatingMsk: null, handoffError: payload.code, retryAt: payload.retryAt ?? null })
  )
  .handleAction(comoLinkActions.programOpenRequested, (state, { payload }) =>
    state[payload.programId]
      ? {
          ...state,
          [payload.programId]: { ...state[payload.programId], openRequests: state[payload.programId].openRequests + 1 }
        }
      : state
  )
  .handleAction(comoLinkActions.handoffCancelled, (state, { payload }) =>
    patchCurrent(state, payload, { navigatingMsk: null })
  )
  .handleAction(
    [comoLinkActions.reset, authDashboardActions.request, authDashboardActions.success],
    () => initialState
  );
